import { useTheme } from '@nexelem/deprecated-theme';
import { Badge, DropdownMenu, IconButton } from '@nexelem/components';
import { FilterData } from '@nexelem/filters';
import { MultipleActionsItemList } from '@nexelem/lists';
import { SavedColDef, GridFiltersProps } from '@nexelem/table';
import { useStore } from 'zustand';
import { useCustomTranslation } from '../../../hooks';
import { openCreateOrEditFilterModal } from '../filters/CreateOrEditFilterModal';
import { openOverrideFilterModal } from '../filters/OverrideFilterModal';
import { openRemoveFilterModal } from '../filters/RemoveFilterModal';
import { faTrash, faEllipsisVertical, faPen, faFloppyDisk } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type SavedFilterProps = Pick<GridFiltersProps, 'store' | 'filters'> & {
  onApplyFilters: (filters: FilterData) => void;
};

export const SavedFilters = ({ store, filters: currentFilters, onApplyFilters }: SavedFilterProps) => {
  const gridSettings = useStore(store, (state) => state.settings);
  const setActiveId = useStore(store, (state) => state.setActiveId);
  const activeId = useStore(store, (state) => state.activeId);
  const { tWrapper } = useCustomTranslation('components.drawers.filterManager');
  const { spacing } = useTheme();
  const listPadding = spacing(2);
  const saveColumns = useStore(store, (state) => state.saveColumns);

  const handleSelectChange = (id?: string, filters?: FilterData, columns?: SavedColDef[]) => () => {
    if (filters && columns && id) {
      onApplyFilters(filters);
      saveColumns(columns);
      setActiveId(id);
    }
  };

  return (
    <div className="flex w-full grow flex-col justify-between overflow-auto">
      {gridSettings.length ? (
        <div
          className="flex grow flex-col gap-2 pr-1 pl-4"
          style={{ paddingTop: listPadding, paddingBottom: listPadding }}
        >
          {gridSettings.map(({ name, description, filters, columns, id, isDefault }) => (
            <MultipleActionsItemList
              isSelected={activeId === id}
              key={id}
              secondaryText={description}
              ActionButton={() =>
                id && (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <IconButton Icon={<FontAwesomeIcon icon={faEllipsisVertical} />} />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="start" side="bottom">
                      <DropdownMenu.Button
                        StartIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
                        onClick={() =>
                          openOverrideFilterModal({
                            filters: currentFilters,
                            id,
                            name,
                            store,
                            onApplyFilters,
                          })
                        }
                      >
                        {tWrapper('override')}
                      </DropdownMenu.Button>
                      <DropdownMenu.Button
                        StartIcon={<FontAwesomeIcon icon={faPen} />}
                        onClick={() =>
                          openCreateOrEditFilterModal({
                            filters: currentFilters,
                            store,
                            editView: { name, id, description, isDefault },
                          })
                        }
                      >
                        {tWrapper('edit')}
                      </DropdownMenu.Button>
                      <DropdownMenu.Button
                        StartIcon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => openRemoveFilterModal({ id, name, store })}
                      >
                        {tWrapper('remove')}
                      </DropdownMenu.Button>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                )
              }
              primaryText={
                <div className="flex items-start gap-2">
                  <span className="text-s16 wrap-break-word">{name}</span>
                  {isDefault && <Badge variant="highlighted">{tWrapper('list.default')}</Badge>}
                </div>
              }
              onSelect={handleSelectChange(id, filters, columns)}
            />
          ))}
        </div>
      ) : (
        <div className="flex h-full items-center justify-center">
          <span className="text-s18">{tWrapper('empty')}</span>
        </div>
      )}
    </div>
  );
};
